// src/Pages/Dashboard/Sales/AddInvoices.jsx
import React, { useState, useEffect } from "react";
import { useInvoices } from "../../../context/InvoicesContext";
import { useNavigate, Link } from "react-router-dom";
import "./AddInvoices.css";
import { API_URL } from "../../../config";

const AddInvoices = () => {
  const { addInvoice } = useInvoices();
  const navigate = useNavigate();

  const [invoiceNo, setInvoiceNo] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [showCustomerWarning, setShowCustomerWarning] = useState(false);

  // NEW: Real customers from DB
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);

  const [formData, setFormData] = useState({
    orderNo: "",
    invoiceDate: new Date().toISOString().split("T")[0],
    terms: "Due on Receipt",
    dueDate: "",
    salesperson: "",
    subject: "",
    items: [
      {
        id: Date.now(),
        description: "",
        qty: 1,
        rate: 0,
        amount: 0,
      },
    ],
    discountType: "%",
    discountValue: 0,
    tdsTcs: false,
    tdsTcsPercent: 0,
    adjustment: 0,
    customerNotes: "Thanks for your business.",
    termsConditions: "",
  });

  // === FETCH NEXT INVOICE NO ===
  useEffect(() => {
    const fetchNextInvoiceNo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/invoices/next-invoice-no`, {
          credentials: "include",
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();

        if (data.success && data.nextInvoiceNo) {
          setInvoiceNo(data.nextInvoiceNo);
        } else {
          console.warn("No invoice no from server, using fallback");
          setInvoiceNo("INV-000001"); // fallback
        }
      } catch (err) {
        console.error("Failed to fetch next invoice no:", err);
        setInvoiceNo("INV-000001"); // Always show something
      }
    };

    fetchNextInvoiceNo();
  }, [API_URL]);

  // === FETCH REAL CUSTOMERS FROM DB ===
  useEffect(() => {
    const backendUrl =
      process.env.REACT_APP_BACKEND_URL ||
      (process.env.NODE_ENV === "production"
        ? "https://finance.dev.marsforce.in"
        : "http://localhost:8030");

    const fetchCustomers = async () => {
      const userId = localStorage.getItem("userId");
      if (!userId) {
        setLoadingCustomers(false);
        return;
      }

      try {
        setLoadingCustomers(true);
        const res = await fetch(
          `${backendUrl}/api/customers/get?userId=${userId}`,
          { credentials: "include" }
        );
        const data = await res.json();

        if (data.success && Array.isArray(data.data)) {
          const sorted = data.data.sort((a, b) => {
            const nameA = getCustomerDisplayName(a).toLowerCase();
            const nameB = getCustomerDisplayName(b).toLowerCase();
            return nameA.localeCompare(nameB);
          });
          setCustomers(sorted);
        } else {
          console.warn("No customers found or invalid data");
        }
      } catch (err) {
        console.error("Failed to load customers:", err);
      } finally {
        setLoadingCustomers(false);
      }
    };

    fetchCustomers();
  }, []);

  // Helper: Get display name for customer
  const getCustomerDisplayName = (cust) => {
    const fullName = [cust.firstName, cust.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    const primary =
      fullName || cust.displayName || cust.companyName || "Unnamed";
    const secondary =
      cust.companyName && cust.companyName !== primary
        ? ` (${cust.companyName})`
        : "";
    return primary + secondary;
  };

  // === ITEM LOGIC ===
  const updateItem = (id, field, value) => {
    setFormData((prev) => {
      const updatedItems = prev.items.map((item) => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          if (field === "qty" || field === "rate") {
            updated.amount =
              Number(updated.qty || 0) * Number(updated.rate || 0);
          }
          return updated;
        }
        return item;
      });
      return { ...prev, items: updatedItems };
    });
  };

  const removeItem = (id) => {
    if (formData.items.length === 1) return;
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.id !== id),
    }));
  };

  const addItemRow = () => {
    const newItem = {
      id: Date.now(),
      description: "",
      qty: 1,
      rate: 0,
      amount: 0,
    };
    setFormData((prev) => ({ ...prev, items: [...prev.items, newItem] }));
  };

  // === CALCULATIONS ===
  const subtotal = formData.items.reduce(
    (sum, item) => sum + (item.amount || 0),
    0
  );
  const discountAmount =
    formData.discountType === "%"
      ? (subtotal * formData.discountValue) / 100
      : formData.discountValue;
  const tdsTcsAmount = formData.tdsTcs
    ? (subtotal * formData.tdsTcsPercent) / 100
    : 0;
  const total = subtotal - discountAmount - tdsTcsAmount + formData.adjustment;

  // === SAVE INVOICE ===
  const handleSave = async (status) => {
    if (!selectedCustomer) {
      setShowCustomerWarning(true);
      return;
    }

    const userId = localStorage.getItem("userId");
    if (!userId) {
      alert("User not logged in");
      return;
    }

    const invoiceData = {
      invoiceNo,
      orderNo: formData.orderNo,
      customerName: selectedCustomer,
      invoiceDate: formData.invoiceDate,
      dueDate: formData.dueDate || formData.invoiceDate,
      terms: formData.terms,
      salesperson: formData.salesperson,
      subject: formData.subject,
      status,
      items: formData.items.map((item) => ({
        description: item.description,
        qty: item.qty,
        rate: item.rate,
        amount: item.amount,
      })),
      discountType: formData.discountType,
      discountValue: formData.discountValue,
      tdsTcs: formData.tdsTcs,
      tdsTcsPercent: formData.tdsTcs ? formData.tdsTcsPercent : 0,
      adjustment: formData.adjustment,
      customerNotes: formData.customerNotes,
      termsConditions: formData.termsConditions,
      amount: total,
      balanceDue: total,
    };

    try {
      const res = await fetch(`${API_URL}/api/invoices/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(invoiceData),
      });

      const data = await res.json();
      if (data.success) {
        navigate("/sales/invoices");
      } else {
        alert("Save failed: " + data.error);
      }
    } catch (err) {
      alert("Network error");
    }
  };

  // === RENDER ===
  return (
    <div className="add-invoice-page">
      <div className="invoice-header">
        <h1>New Invoice</h1>
      </div>

      <div className="invoice-form">
        {/* === CUSTOMER & INVOICE# === */}
        <div className="form-row top-row">
          <div className="form-group customer-group">
            <label>
              Customer Name <span className="required">*</span>
            </label>
            <select
              value={selectedCustomer}
              onChange={(e) => {
                setSelectedCustomer(e.target.value);
                setShowCustomerWarning(false);
              }}
              disabled={loadingCustomers}
              className="customer-select"
            >
              <option value="">
                {loadingCustomers
                  ? "Loading customers..."
                  : "Select a customer"}
              </option>
              {customers.map((cust) => (
                <option key={cust._id} value={getCustomerDisplayName(cust)}>
                  {getCustomerDisplayName(cust)}
                </option>
              ))}
            </select>

            {/* Add New Customer Link */}
            <div className="add-customer-link">
              <Link to="/sales/customers/new" className="link">
                + Add New Customer
              </Link>
            </div>

            {showCustomerWarning && (
              <small className="warning">
                Please select a customer to create invoice
              </small>
            )}
          </div>

          <div className="form-group">
            <label>Invoice#</label>
            <input type="text" value={invoiceNo} readOnly />
          </div>
        </div>

        {/* === ORDER, DATE, TERMS, DUE === */}
        <div className="form-row">
          <div className="form-group">
            <label>Order Number</label>
            <input
              type="text"
              value={formData.orderNo}
              onChange={(e) =>
                setFormData({ ...formData, orderNo: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Invoice Date</label>
            <input
              type="date"
              value={formData.invoiceDate}
              onChange={(e) =>
                setFormData({ ...formData, invoiceDate: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Terms</label>
            <select
              value={formData.terms}
              onChange={(e) =>
                setFormData({ ...formData, terms: e.target.value })
              }
            >
              <option>Due on Receipt</option>
              <option>Net 15</option>
              <option>Net 30</option>
              <option>Net 45</option>
              <option>Net 60</option>
            </select>
          </div>
          <div className="form-group">
            <label>Due Date</label>
            <input
              type="date"
              value={formData.dueDate}
              onChange={(e) =>
                setFormData({ ...formData, dueDate: e.target.value })
              }
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Salesperson</label>
            <input
              type="text"
              placeholder="Select or Add Salesperson"
              value={formData.salesperson}
              onChange={(e) =>
                setFormData({ ...formData, salesperson: e.target.value })
              }
            />
          </div>
          <div className="form-group full-width">
            <label>Subject</label>
            <input
              type="text"
              placeholder="Let your customer know what this invoice is for"
              value={formData.subject}
              onChange={(e) =>
                setFormData({ ...formData, subject: e.target.value })
              }
            />
          </div>
        </div>

        {/* === ITEM TABLE === */}
        <div className="item-section">
          <div className="section-header">
            <h3>Item Table</h3>
            <div className="actions">
              <button className="action-btn">Scan Item</button>
              <button className="action-btn">Bulk Actions</button>
            </div>
          </div>

          <table className="item-table">
            <thead>
              <tr>
                <th>ITEM DETAILS</th>
                <th>QUANTITY</th>
                <th>RATE</th>
                <th>AMOUNT</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {formData.items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <input
                      type="text"
                      placeholder="Type or click to select an item"
                      value={item.description}
                      onChange={(e) =>
                        updateItem(item.id, "description", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="1"
                      value={item.qty}
                      onChange={(e) =>
                        updateItem(item.id, "qty", Number(e.target.value) || 0)
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      value={item.rate}
                      onChange={(e) =>
                        updateItem(item.id, "rate", Number(e.target.value) || 0)
                      }
                    />
                  </td>
                  <td className="amount">₹{item.amount.toFixed(2)}</td>
                  <td>
                    {formData.items.length > 1 && (
                      <button
                        className="delete-btn"
                        onClick={() => removeItem(item.id)}
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="add-row-actions">
            <button onClick={addItemRow} className="add-row-btn">
              + Add New Row
            </button>
            <button className="add-bulk-btn">+ Add Items in Bulk</button>
          </div>
        </div>

        {/* === CALCULATIONS === */}
        <div className="calculations">
          <div className="notes-group">
            <label>Customer Notes</label>
            <textarea
              value={formData.customerNotes}
              onChange={(e) =>
                setFormData({ ...formData, customerNotes: e.target.value })
              }
            />
            <small>Will be displayed on the invoice</small>
          </div>
          <div className="calc-left">
            <div className="calc-row">Sub Total</div>
            <div className="calc-row">
              <span>Discount</span>
              <input
                type="number"
                value={formData.discountValue}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    discountValue: Number(e.target.value) || 0,
                  })
                }
              />
              <select
                value={formData.discountType}
                onChange={(e) =>
                  setFormData({ ...formData, discountType: e.target.value })
                }
              >
                <option>₹</option>
                <option>%</option>
              </select>
            </div>
            <div className="calc-row">
              <label>
                <input
                  type="checkbox"
                  checked={formData.tdsTcs}
                  onChange={(e) =>
                    setFormData({ ...formData, tdsTcs: e.target.checked })
                  }
                />{" "}
                TDS / TCS
              </label>
              <select
                disabled={!formData.tdsTcs}
                value={formData.tdsTcsPercent}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tdsTcsPercent: Number(e.target.value),
                  })
                }
              >
                <option value="">Select a Tax</option>
                <option value={5}>5%</option>
                <option value={10}>10%</option>
              </select>
            </div>
            <div className="calc-row">
              <span>Adjustment</span>
              <input
                type="number"
                value={formData.adjustment}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    adjustment: Number(e.target.value) || 0,
                  })
                }
              />
            </div>
            <div className="calc-row total">Total (₹)</div>
          </div>
          <div className="calc-right">
            <div>₹{subtotal.toFixed(2)}</div>
            <div>-₹{discountAmount.toFixed(2)}</div>
            <div>-₹{tdsTcsAmount.toFixed(2)}</div>
            <div>₹{formData.adjustment.toFixed(2)}</div>
            <div className="grand-total">₹{total.toFixed(2)}</div>
          </div>
        </div>

        {/* === NOTES & ATTACHMENTS === */}
        <div className="bottom-section">
          <div className="terms-group">
            <label>Terms & Conditions</label>
            <textarea
              placeholder="Enter the terms and conditions..."
              value={formData.termsConditions}
              onChange={(e) =>
                setFormData({ ...formData, termsConditions: e.target.value })
              }
            />
          </div>
          <div className="attach-group">
            <label>Attach File(s) to Invoice</label>
            <div className="upload-box">
              <input type="file" multiple />
              <p>You can upload a maximum of 10 files, 10MB each</p>
            </div>
          </div>
        </div>

        {/* === ACTION BAR === */}
        <div className="action-bar">
          <div className="left-actions">
            <button onClick={() => handleSave("DRAFT")} className="draft-btn">
              Save as Draft
            </button>
            <button onClick={() => handleSave("SENT")} className="send-btn">
              Save and Send
            </button>
            <button
              onClick={() => navigate("/sales/invoices")}
              className="cancel-btn"
            >
              Cancel
            </button>
          </div>
          <div className="right-actions">
            <button className="recurring-btn">Make Recurring</button>
            <div className="total-info">
              <span>Total Amount: ₹{total.toFixed(2)}</span>
              <span>
                Total Quantity: {formData.items.reduce((s, i) => s + i.qty, 0)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddInvoices;
